import { useState, useEffect, useCallback } from 'react';
import { 
  WarehouseHierarchyConfig, 
  DEFAULT_HIERARCHY_CONFIG,
  HierarchyLevel 
} from '@/types/warehouse-config';
import { useGraphQLFetch } from './use-graphql-fetch';

const STORAGE_KEY = 'warehouse-hierarchy-config';

interface LocationHierarchyLevelFromAPI {
  levelID: number;
  levelNumber: number;
  displayName: string;
  codePrefix: string | null;
  isActive: boolean;
}

const GET_LOCATION_HIERARCHY_CONFIG = `
  query GetLocationHierarchyConfig {
    locationHierarchy {
      locationHierarchyConfig {
        levelID
        levelNumber
        displayName
        codePrefix
        isActive
      }
    }
  }
`;

interface UseWarehouseHierarchyConfigOptions {
  isLive?: boolean;
}

/**
 * Hook to manage warehouse hierarchy configuration
 * Provides access to the current hierarchy configuration and helper functions
 * When isLive is true, fetches from GraphQL API; otherwise uses localStorage
 */
export function useWarehouseHierarchyConfig(options: UseWarehouseHierarchyConfigOptions = {}) {
  const { isLive = false } = options;
  const { fetchGraphQL } = useGraphQLFetch();
  const [isLoading, setIsLoading] = useState(false);
  
  const [config, setConfig] = useState<WarehouseHierarchyConfig>(() => {
    // Load from localStorage on mount
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          
          // Migration: Ensure all 5 levels exist and are active
          // If the config has fewer than 5 levels, add missing levels from default
          const existingLevels = parsed.levels || [];
          const allLevels = DEFAULT_HIERARCHY_CONFIG.levels.map(defaultLevel => {
            const existingLevel = existingLevels.find((l: HierarchyLevel) => l.level === defaultLevel.level);
            if (existingLevel) {
              // Use existing level but ensure it's active
              return {
                ...existingLevel,
                isActive: true,
              };
            } else {
              // Add missing level from default
              return {
                ...defaultLevel,
                isActive: true,
              };
            }
          });
          
          const migrated = {
            ...parsed,
            numberOfLevels: 5,
            levels: allLevels,
          };
          
          // Save the migrated config back to localStorage
          localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
          return migrated;
        } catch (e) {
          console.error('Failed to parse warehouse hierarchy config:', e);
        }
      }
    }
    // Return default with all 5 levels active
    const defaultConfig = {
      ...DEFAULT_HIERARCHY_CONFIG,
      numberOfLevels: 5,
      levels: DEFAULT_HIERARCHY_CONFIG.levels.map(level => ({
        ...level,
        isActive: true,
      })),
    };
    console.log('Using default config:', defaultConfig);
    return defaultConfig;
  });

  // Save to localStorage whenever config changes (only in demo mode)
  useEffect(() => {
    if (typeof window !== 'undefined' && !isLive) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }
  }, [config, isLive]);

  // Fetch config from GraphQL API when in live mode
  const fetchConfigFromAPI = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchGraphQL<{
        locationHierarchy: {
          locationHierarchyConfig: LocationHierarchyLevelFromAPI[];
        };
      }>(GET_LOCATION_HIERARCHY_CONFIG);

      if (response.errors) {
        console.error("GraphQL errors:", response.errors);
        return;
      }

      if (response.data?.locationHierarchy?.locationHierarchyConfig) {
        const apiLevels = response.data.locationHierarchy.locationHierarchyConfig;
        const levels: HierarchyLevel[] = apiLevels.map((level) => ({
          level: level.levelNumber,
          displayName: level.displayName,
          codePrefix: level.codePrefix || "",
          isActive: level.isActive,
        }));

        const newConfig: WarehouseHierarchyConfig = {
          numberOfLevels: 5,
          levels,
        };

        setConfig(newConfig);
      }
    } catch (error) {
      console.error("Failed to fetch hierarchy config from API:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fetchGraphQL]);

  // Fetch from API when in live mode
  useEffect(() => {
    if (isLive) {
      fetchConfigFromAPI();
    }
  }, [isLive, fetchConfigFromAPI]);

  // Listen for hierarchy config updates from other components (e.g., settings page)
  useEffect(() => {
    const handleConfigUpdate = () => {
      if (isLive) {
        fetchConfigFromAPI();
      }
    };

    window.addEventListener('hierarchy-config-updated', handleConfigUpdate);
    return () => {
      window.removeEventListener('hierarchy-config-updated', handleConfigUpdate);
    };
  }, [isLive, fetchConfigFromAPI]);

  // Get active levels only
  const activeLevels = config.levels
    .filter(level => level.isActive)
    .sort((a, b) => a.level - b.level);

  // Get level by number
  const getLevelByNumber = (levelNumber: number): HierarchyLevel | undefined => {
    return config.levels.find(l => l.level === levelNumber);
  };

  // Get active level display name
  const getLevelDisplayName = (levelNumber: number): string => {
    const level = getLevelByNumber(levelNumber);
    return level?.displayName || `Level ${levelNumber}`;
  };

  // Get active level code prefix
  const getLevelCodePrefix = (levelNumber: number): string => {
    const level = getLevelByNumber(levelNumber);
    return level?.codePrefix || '';
  };

  // Check if a level is active
  const isLevelActive = (levelNumber: number): boolean => {
    const level = getLevelByNumber(levelNumber);
    return level?.isActive || false;
  };

  // Generate location code from hierarchy parts
  const generateLocationCode = (parts: { [key: number]: string }): string => {
    const codeParts: string[] = [];
    
    activeLevels.forEach(level => {
      const value = parts[level.level];
      if (value) {
        const prefix = level.codePrefix.trim();
        if (prefix) {
          codeParts.push(`${prefix}${value}`);
        } else {
          codeParts.push(value);
        }
      }
    });

    return codeParts.join('-');
  };

  // Generate breadcrumb from hierarchy parts
  const generateBreadcrumb = (parts: { [key: number]: string }): string => {
    const breadcrumbParts: string[] = [];
    
    activeLevels.forEach(level => {
      const value = parts[level.level];
      if (value) {
        breadcrumbParts.push(value);
      }
    });

    return breadcrumbParts.join(' > ');
  };

  // Get column definitions for active levels
  const getActiveColumnDefinitions = () => {
    return activeLevels.map(level => ({
      id: `level${level.level}`,
      name: level.displayName,
      description: `${level.displayName} code`,
      level: level.level,
    }));
  };

  return {
    config,
    setConfig,
    activeLevels,
    numberOfLevels: config.numberOfLevels,
    capacityTrackingEnabled: config.capacityTrackingEnabled,
    getLevelByNumber,
    getLevelDisplayName,
    getLevelCodePrefix,
    isLevelActive,
    generateLocationCode,
    generateBreadcrumb,
    getActiveColumnDefinitions,
    isLoading,
    refetch: fetchConfigFromAPI,
  };
}
